import React from "react";
import PolicyPage from "../../components/policy/PolicyPage";
import { BUSINESS } from "../../config/business";

export default function RefundPolicy() {
  const sections = [
    {
      heading: "Summary — please read this first",
      body: [
        {
          note:
            "We do not offer change-of-mind returns or refunds on delivered products. Once a product has been handed over, it is covered by the manufacturer's warranty, not by a return. You can, however, cancel a booking before handover and get your advance back under the terms below.",
        },
        `This policy explains three separate things, which are often confused:`,
        {
          list: [
            "Cancelling a booking before you take delivery — covered in sections 2 and 3.",
            "Getting money back, and how long that takes — covered in sections 5 to 7.",
            "A product that is faulty or damaged — that is a warranty matter, not a refund. See our Warranty & Returns Policy.",
          ],
        },
        `All refunds are made in ${BUSINESS.currencyLabel} only, to the original payment method. We do not refund in cash against an online payment, and we do not refund to a different account or card from the one used to pay.`,
      ],
    },
    {
      heading: "Cancelling a product booking",
      body: [
        `When you pay a booking advance online, we reserve a specific unit in your name, and in many cases we order it in, assemble it, charge and calibrate the battery and carry out a pre-delivery inspection for you. What you get back on cancellation depends on how far that process has gone.`,
        {
          table: {
            head: ["When you cancel", "What you get back"],
            rows: [
              [
                "Within 24 hours of paying the advance, and before the unit has been despatched to us or prepared",
                "100% of the advance refunded",
              ],
              [
                "After 24 hours, but before the unit has been assembled and pre-delivery inspected",
                "100% of the advance refunded, less any actual third-party charge already incurred on your order (for example a special-order freight charge), which we will evidence to you",
              ],
              [
                "After the unit has been assembled, registered to you and made ready for handover",
                "Advance refunded less a preparation charge of up to 10% of the advance, capped at ₹1,000",
              ],
              [
                "On a made-to-order, special-order or customised product, at any point after we have placed the order with the manufacturer",
                "Advance is not refundable, because the unit cannot be returned to the manufacturer or resold as new. We will tell you clearly, before you pay, when a product falls into this category",
              ],
              [
                "You do not pay the balance or collect the product within the period stated in your booking confirmation, and do not respond to two reminders",
                "We may release the unit to another customer. The advance is refunded less a preparation charge of up to 10%, capped at ₹1,000",
              ],
            ],
          },
        },
        `To cancel, email ${BUSINESS.email} or call ${BUSINESS.phone} quoting your booking or order number. We confirm every cancellation in writing. A cancellation is effective only once we have confirmed it — a message left unanswered is not a cancellation.`,
      ],
    },
    {
      heading: "Cancellation by us",
      body: [
        `Occasionally we have to cancel an order. This happens where the product is out of stock or discontinued, where the manufacturer cannot supply within a reasonable time, where a pricing or listing error has occurred, where your address falls outside our service area, or where we cannot verify a payment.`,
        `Where we cancel, you receive a 100% refund of everything you have paid, with no deduction of any kind, and we will offer you an alternative product or a revised delivery date first if one is available.`,
      ],
    },
    {
      heading: "Cancelling a service or repair job",
      body: [
        {
          list: [
            "You may cancel a booked service appointment at no charge up to 24 hours before the appointment.",
            "You may cancel a repair job free of charge at any point before we begin work or order a part for it.",
            "Once a spare part has been ordered specifically for your vehicle, the cost of that part is payable even if you cancel, because it cannot be returned. We will tell you the part cost before ordering.",
            "Once labour has commenced, charges accrue for the work already carried out. We will show you what was done and invoice only for that.",
            "Diagnostic or inspection charges, where applicable and disclosed in advance, are payable once the diagnosis has been carried out, whether or not you proceed with the repair.",
          ],
        },
        `Advance payments taken for a service or repair are adjusted against the final invoice. Any excess is refunded to you under the timelines in section 6.`,
      ],
    },
    {
      heading: "When a refund is payable",
      body: [
        `A refund is payable in the following situations, and only these:`,
        {
          list: [
            "You cancel a booking within the windows set out in section 2.",
            "We cancel your order for any reason (section 3).",
            "We are unable to deliver the product within 30 days of the confirmed delivery date and you choose not to wait.",
            "You have been charged twice for the same order, or charged an incorrect amount, due to a payment or system error.",
            "Money was debited from your account but the order did not complete — see section 7.",
            "An excess remains after a service or repair invoice is settled.",
            "A product is found on delivery to be damaged in transit or materially different from what you ordered, and we are unable to replace it — see our Warranty & Returns Policy for the inspection procedure that applies.",
          ],
        },
        `No refund is payable for change of mind, for a product that does not suit you after use, for a product damaged after handover, for range or performance that differs from the manufacturer's indicative figure due to riding conditions, or for a warranty issue that the manufacturer has repaired or replaced.`,
      ],
    },
    {
      heading: "How refunds are processed and how long they take",
      body: [
        `Once we approve a refund, we initiate it from our side within 5 working days. The time it then takes to reach you depends on your bank or payment provider, and that part is outside our control.`,
        {
          table: {
            head: ["Original payment method", "Refund goes to", "Typical time after we initiate"],
            rows: [
              [
                "UPI",
                "The same UPI ID / linked bank account",
                "1 to 3 working days",
              ],
              [
                "Debit or credit card",
                "The same card",
                "5 to 10 working days, depending on the card issuer",
              ],
              [
                "Net banking",
                "The same bank account",
                "3 to 7 working days",
              ],
              [
                "EMI / consumer finance",
                "The financier, who then adjusts or closes your loan",
                "7 to 21 working days; the financier's own charges may apply",
              ],
              [
                "Cash or card paid at the showroom",
                "By NEFT / UPI to your bank account, on receipt of your details",
                "3 to 7 working days",
              ],
            ],
          },
        },
        {
          note:
            "For an EMI-funded purchase, the refund goes to the financier and not to you directly. Interest already accrued, processing fees and any foreclosure charge levied by the financier are governed by your loan agreement with them and are not refunded by us.",
        },
        `We send you a refund reference number by email or WhatsApp when the refund is initiated. If the money has not reached you within the window above, quote that reference to your bank first, then contact us and we will follow it up with the payment gateway.`,
      ],
    },
    {
      heading: "Failed payments and money debited without an order",
      body: [
        `If money leaves your account but you do not receive an order confirmation, the transaction has usually failed at the gateway and the amount is auto-reversed by your bank, typically within 5 to 7 working days, without any action from us.`,
        `If it has not been reversed after 7 working days, contact us with the date, the amount and the bank reference or UTR number. We will trace it with our payment gateway and confirm the outcome to you within 7 working days of your report.`,
        `Please do not attempt the payment again until you have checked whether the first one went through, and do not treat a pending bank SMS as confirmation of an order. Only a written order confirmation from us confirms an order.`,
      ],
    },
    {
      heading: "Chargebacks",
      body: [
        `If you believe a charge is wrong, please contact us before raising a chargeback with your bank. Most disputes are resolved faster directly, and a chargeback locks the amount for weeks while the banks investigate.`,
        `Where a chargeback is raised on an order we have already fulfilled, we will contest it with delivery evidence, the signed handover record and the invoice. Raising a chargeback in bad faith on a delivered product may lead us to decline future orders and to recover the amount by lawful means.`,
      ],
    },
    {
      heading: "Your statutory rights",
      body: [
        `Nothing in this policy limits your rights under the Consumer Protection Act, 2019 or any other law that cannot be excluded by agreement. In particular, your right to a remedy for goods that are defective, unsafe, or not as described is unaffected by the fact that we do not offer change-of-mind returns.`,
        `If you are not satisfied with how we have handled a refund or cancellation, escalate it to us in writing at ${BUSINESS.email}. We acknowledge within 48 working hours and aim to resolve within 15 working days. You may also approach the National Consumer Helpline or the consumer forum having jurisdiction.`,
      ],
    },
  ];

  return (
    <PolicyPage
      title="Refund & Cancellation Policy"
      currentPath="/refund-policy"
      intro={`How to cancel a booking, when a refund is payable, how much you get back, and how long the money takes to reach you. All refunds are made in ${BUSINESS.currencyLabel} to the original payment method.`}
      sections={sections}
    />
  );
}
